/**
 * cronApi.ts — the Settings → Cron tab's REST client.
 *
 * Thin fetch wrappers over `/api/projects/{id}/cron…` (see
 * `backend/routers/cron.py`). The desired state (jobs + settings) is saved
 * through here and the backend reconciles the platform's cron jobs from it;
 * `CronState.platform` carries what the platform actually holds right now
 * (drift the Sync button repairs). Free functions taking `(origin, token)`
 * — same shape as `../chat/aiConfig`.
 */

/** One settings-managed cron entry — the manifest's `CronJobSpec`. `task` +
 * `target` identify a Tasks-panel row (the trainer/model name for the
 * per-module kinds); `action` mirrors the panel's Run/Stop/Restart. */
export type CronTaskKind = 'model' | 'notebook' | 'trainer' | 'mlmodel' | 'workflows' | 'images' | 'task';
export type CronTaskAction = 'start' | 'stop' | 'restart';

export interface CronJob {
  id: string;
  label?: string | null;
  /** 5-field cron expression (minute hour day month weekday). */
  schedule: string;
  task: CronTaskKind;
  /** Trainer/model name (the per-module kinds — a project may ship several). */
  target?: string | null;
  action: CronTaskAction;
  seed?: number | null;
  enabled: boolean;
}

/** One row of the Tasks panel's poll (`GET /api/projects/{id}/tasks`) — the
 * picker's options: every task this project actually has, same identity the
 * panel uses (kind + trainer/model/task name). */
export interface TaskRow {
  kind: string;
  label: string;
  trainer?: string;
  model?: string;
  task?: string;
  entries?: string[];
}

/** A task row's identity — `kind` for singleton rows, `kind:name` for the
 * per-module kinds (mirrors the panel's rowKey). */
export function taskRowKey(row: { kind: string; trainer?: string; model?: string; task?: string }): string {
  if (row.trainer) return `${row.kind}:${row.trainer}`;
  if (row.model) return `${row.kind}:${row.model}`;
  if (row.task) return `${row.kind}:${row.task}`;
  return row.kind;
}

/** A platform-side cron job as the GET returns it (ours are name-prefixed
 * `vskilab--<project>--…`). */
export interface CronPlatformJob {
  name: string;
  schedule?: string;
  url?: string;
  method?: string;
  type?: string;
  enabled?: boolean;
}

/** What a reconcile pass did (per-job failures collected, not raised). */
export interface CronSync {
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
  /** Present when the platform was unreachable — the desired state still
   * saved; the sync replays on the next mutation or Sync-now. */
  error?: string;
}

/** GET /cron response shape. */
export interface CronState {
  baseUrl?: string | null;
  /** What's used when `baseUrl` is empty (env override / PROJECT_BASE_URL). */
  defaultBaseUrl: string;
  hookToken?: string | null;
  jobs: CronJob[];
  platform: { jobs: CronPlatformJob[]; error: string | null };
}

/** 5-field cron expression check (mirrors the backend's manifest validator —
 * the platform remains the final validator). */
const CRON_RE =
  /^(\*|\d+(-\d+)?(\/\d+)?(,\d+(-\d+)?(\/\d+)?)*)(\s+(\*|\d+(-\d+)?(\/\d+)?(,\d+(-\d+)?(\/\d+)?)*)){4}$/;

export function cronScheduleError(expr: string): string | null {
  if (!CRON_RE.test(expr.trim())) {
    return "Use a 5-field cron expression (minute hour day month weekday), e.g. '*/15 * * * *'";
  }
  return null;
}

async function request(
  origin: string,
  token: string | null,
  method: string,
  path: string,
  body?: unknown,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${origin}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.json()) as { detail?: unknown };
      if (typeof j?.detail === 'string') detail = j.detail;
    } catch { /* non-JSON error body — keep the status line */
    }
    throw new Error(detail);
  }
  return (await res.json()) as Record<string, unknown>;
}

/** The whole cron state: settings + jobs + the platform-side view. */
export async function getCron(origin: string, token: string | null, id: string): Promise<CronState> {
  return (await request(origin, token, 'GET', `/api/projects/${encodeURIComponent(id)}/cron`)) as unknown as CronState;
}

/** Replace the whole job list; the backend reconciles the platform to it. */
export async function putCronJobs(
  origin: string, token: string | null, id: string, jobs: CronJob[],
): Promise<CronState & { sync: CronSync }> {
  return (await request(
    origin, token, 'PUT', `/api/projects/${encodeURIComponent(id)}/cron/jobs`, { jobs },
  )) as unknown as CronState & { sync: CronSync };
}

/** Persist the hook base URL override (null/'' = use the server default). */
export async function putCronSettings(
  origin: string, token: string | null, id: string, baseUrl: string | null,
): Promise<CronState & { sync: CronSync }> {
  return (await request(
    origin, token, 'PUT', `/api/projects/${encodeURIComponent(id)}/cron/settings`,
    { baseUrl: baseUrl || null },
  )) as unknown as CronState & { sync: CronSync };
}

/** Mint a fresh hook token (every platform job's header follows). */
export async function rotateCronToken(
  origin: string, token: string | null, id: string,
): Promise<CronState & { sync: CronSync }> {
  return (await request(
    origin, token, 'POST', `/api/projects/${encodeURIComponent(id)}/cron/token`,
  )) as unknown as CronState & { sync: CronSync };
}

/** Reconcile now (drift repair — e.g. after the platform was down). */
export async function syncCron(
  origin: string, token: string | null, id: string,
): Promise<{ sync: CronSync }> {
  return (await request(
    origin, token, 'POST', `/api/projects/${encodeURIComponent(id)}/cron/sync`,
  )) as unknown as { sync: CronSync };
}

/** The project's trainers (the `trainer.start` action's target picker). */
export async function listTrainerNames(
  origin: string, token: string | null, id: string,
): Promise<string[]> {
  const data = (await request(
    origin, token, 'GET', `/api/projects/${encodeURIComponent(id)}/trainers`,
  )) as { trainers?: { id?: string; name?: string }[] };
  return (data.trainers ?? []).map((t) => t.id ?? t.name ?? '').filter(Boolean);
}

/** The project's task rows — the same poll the Code tab's Tasks panel
 * renders. The cron picker's options: schedule ANY task the project has. */
export async function listTasks(
  origin: string, token: string | null, id: string,
): Promise<TaskRow[]> {
  const data = (await request(
    origin, token, 'GET', `/api/projects/${encodeURIComponent(id)}/tasks`,
  )) as { tasks?: TaskRow[] };
  return data.tasks ?? [];
}
